import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { X, BookOpen, FileText } from 'lucide-react';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';
import { useDialogA11y } from '../ui/useDialogA11y';

interface AssignMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  materialId: string;
  materialTitle: string;
}

interface Course {
  id: string;
  name: string;
}

interface Module {
  id: string;
  name: string;
  course_id: string;
}

export function AssignMaterialModal({
  isOpen,
  onClose,
  onSuccess,
  materialId,
  materialTitle,
}: AssignMaterialModalProps) {
  const confirm = useConfirm();
  const [courses, setCourses] = useState<Course[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [existingAssignments, setExistingAssignments] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (isOpen) {
      loadCourses();
      loadExistingAssignments();
    }
  }, [isOpen, materialId]);

  useEffect(() => {
    if (selectedCourse) {
      loadModules(selectedCourse);
    } else {
      setModules([]);
      setSelectedModules([]);
    }
  }, [selectedCourse]);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadModules = async (courseId: string) => {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('id, name, course_id')
        .eq('course_id', courseId)
        .order('order_index');

      if (error) throw error;
      setModules(data || []);
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  };

  const loadExistingAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('module_materials')
        .select('module_id')
        .eq('material_id', materialId);

      if (error) throw error;
      setExistingAssignments(data?.map((a) => a.module_id) || []);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const toggleModuleSelection = (moduleId: string) => {
    setSelectedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      for (const moduleId of selectedModules) {
        if (!existingAssignments.includes(moduleId)) {
          const { error } = await supabase.from('module_materials').insert({
            module_id: moduleId,
            material_id: materialId,
          });

          if (error) throw error;
        }
      }

      const modulesToRemove = existingAssignments.filter(
        (moduleId) =>
          modules.some((m) => m.id === moduleId) &&
          !selectedModules.includes(moduleId)
      );

      for (const moduleId of modulesToRemove) {
        const { error } = await supabase
          .from('module_materials')
          .delete()
          .eq('module_id', moduleId)
          .eq('material_id', materialId);

        if (error) throw error;
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error assigning material:', error);
      setError(error.message || 'Error al asignar material');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignAll = async () => {
    if (!selectedCourse) return;

    const ok = await confirm({
      title: 'Desasignar material',
      description: 'Se desasignará este material de TODOS los módulos del curso seleccionado. Esta acción no se puede deshacer.',
      confirmText: 'Desasignar',
      tone: 'danger',
    });
    if (!ok) return;

    setLoading(true);
    setError('');

    try {
      const moduleIds = modules.map(m => m.id);

      const { error } = await supabase
        .from('module_materials')
        .delete()
        .eq('material_id', materialId)
        .in('module_id', moduleIds);

      if (error) throw error;

      await loadExistingAssignments();
      setSelectedModules([]);

      toast.success('Material desasignado de todos los módulos del curso');
      onSuccess();
    } catch (error: any) {
      console.error('Error unassigning material:', error);
      setError(error.message || 'Error al desasignar material');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedCourse('');
    setSelectedModules([]);
    setError('');
  };

  useEffect(() => {
    if (modules.length > 0) {
      const currentCourseModules = modules.map((m) => m.id);
      const assigned = existingAssignments.filter((id) =>
        currentCourseModules.includes(id)
      );
      setSelectedModules(assigned);
    }
  }, [modules, existingAssignments]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-assignmaterial"
        tabIndex={-1}
        className="glass-surface rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 glass-surface border-b border-white/[0.08] p-6 flex items-center justify-between">
          <div>
            <h2 id="modal-title-assignmaterial" className="text-display text-2xl font-bold text-white">Asignar Material</h2>
            <p className="text-muted mt-1 text-sm">{materialTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-all"
          >
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Seleccionar Curso *
              </div>
            </label>
            <select
              required
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
            >
              <option value="">-- Selecciona un curso --</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>

          {selectedCourse && modules.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-white/70 mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Seleccionar Módulos
                </div>
              </label>

              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {modules.map((module) => {
                  const isSelected = selectedModules.includes(module.id);
                  const isAlreadyAssigned = existingAssignments.includes(
                    module.id
                  );

                  return (
                    <label
                      key={module.id}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-cubus-lime/10 border-cubus-lime text-cubus-lime'
                          : 'glass-deep border-white/[0.08] text-white/70 hover:border-white/[0.1]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleModuleSelection(module.id)}
                        className="w-4 h-4 rounded border-white/[0.1] text-cubus-lime focus:ring-cubus-lime focus:ring-offset-0"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{module.name}</span>
                        {isAlreadyAssigned && !isSelected && (
                          <span className="ml-2 text-xs text-red-400">
                            (Se eliminará)
                          </span>
                        )}
                        {isAlreadyAssigned && isSelected && (
                          <span className="ml-2 text-xs text-subtle">
                            (Ya asignado)
                          </span>
                        )}
                        {!isAlreadyAssigned && isSelected && (
                          <span className="ml-2 text-xs text-cubus-lime">
                            (Nuevo)
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <p className="text-xs text-subtle mt-3">
                Selecciona los módulos donde quieres que aparezca este material
              </p>
            </div>
          )}

          {selectedCourse && modules.length === 0 && (
            <div className="text-center py-8 text-subtle">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Este curso no tiene módulos aún</p>
            </div>
          )}

          {selectedCourse && modules.length > 0 && existingAssignments.filter(id => modules.some(m => m.id === id)).length > 0 && (
            <div className="pb-4 border-t border-white/[0.08] pt-4">
              <button
                type="button"
                onClick={handleUnassignAll}
                disabled={loading}
                className="w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Desasignar de Todos los Módulos del Curso
              </button>
              <p className="text-xs text-subtle mt-2 text-center">
                Esto eliminará este material de todos los módulos del curso seleccionado
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !selectedCourse}
              className="flex-1 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
